/**
 * TeamSystem Impact - Agent Token Tab
 * Tab content per gestione token MCP in Settings.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
import { agentTokenApi, AgentTokenDTO } from '../api/agentToken.api';
import { CreateTokenDialog } from './CreateTokenDialog';
import { RevokeTokenDialog } from './RevokeTokenDialog';

function formatDate(dateStr?: string): string {
  if (!dateStr) return i18n.t('settings.tokens.never');
  return new Date(dateStr).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const SCOPE_COLORS: Record<string, 'primary' | 'secondary' | 'success' | 'warning' | 'info'> = {
  'dma:read': 'primary',
  'dma:write': 'primary',
  'pm:read': 'success',
  'pm:write': 'success',
  'datacapture:read': 'info',
  'datacapture:write': 'info',
  'engagement:read': 'warning',
  'engagement:write': 'warning',
};

export function AgentTokenTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<{ agentId: string; agentName: string } | null>(
    null,
  );

  const {
    data: tokens,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['agent-tokens'],
    queryFn: agentTokenApi.list,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['agent-tokens'] });
  };

  return (
    <Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
      >
        <Typography variant="h6">{t('settings.tokens.sectionTitle')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          size="small"
        >
          {t('settings.tokens.newToken')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('settings.tokens.loadError')}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tokens && tokens.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.tokens.colName')}</TableCell>
                <TableCell>{t('settings.tokens.colPermissions')}</TableCell>
                <TableCell>{t('settings.tokens.colCreatedAt')}</TableCell>
                <TableCell>{t('settings.tokens.colLastUsed')}</TableCell>
                <TableCell align="right">{t('settings.tokens.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((token: AgentTokenDTO) => (
                <TableRow key={token.agentId}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {token.agentName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {token.scopes.map((scope) => (
                        <Chip
                          key={scope}
                          label={scope}
                          size="small"
                          color={SCOPE_COLORS[scope] || 'default'}
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(token.createdAt)}</TableCell>
                  <TableCell>{formatDate(token.lastUsedAt)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('settings.tokens.revokeTooltip')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          setRevokeTarget({
                            agentId: token.agentId,
                            agentName: token.agentName,
                          })
                        }
                        aria-label={t('settings.tokens.revokeAria', { name: token.agentName })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {t('settings.tokens.noTokens')}
          </Typography>
        </Paper>
      )}

      <CreateTokenDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={handleRefresh}
      />

      {revokeTarget && (
        <RevokeTokenDialog
          open={true}
          agentId={revokeTarget.agentId}
          agentName={revokeTarget.agentName}
          onClose={() => setRevokeTarget(null)}
          onSuccess={handleRefresh}
        />
      )}
    </Box>
  );
}
